import { Action, ActionReducer } from '@ngrx/store';
import { STORAGE_KEYS, StorageService } from '@core/services/storage/storage.service';
import { storeInitialState, StoreState } from '@store/store.state';
import { userActions } from '@store/actions/user.actions';
import { CustomRecentInputKeys } from '@shared/forms/custom-recent-input/data/custom-recent-input-keys.directory';
import { appActions } from '@store/actions/app.actions';
import { AuthConfigIds } from '../../auth/auth.config';
import { AvailableLanguages } from '@core/components/header/lang-selection/lang-selection.config';
import { defaultLanguageFn } from '@core/services/translation/default-lang';
import { UserCountry } from '@core/domain/model/user/auth-user.model';

let isRehydrated = false;

export const storageMetaReducer =
  (storageService: StorageService) =>
  (reducer: ActionReducer<StoreState>) =>
  (state: StoreState, action: Action): StoreState => {
    if (!isRehydrated && action.type === appActions.hydrateApp.type) {
      isRehydrated = true;
      const storageLanguage = (storageService.getItem(STORAGE_KEYS.LANGUAGE) as AvailableLanguages) ?? state.language.lang;
      const storageImpersonationToken = storageService.getItem(STORAGE_KEYS.IMPERSONATION_TOKEN) ?? null;
      let recentInputs: Partial<Record<CustomRecentInputKeys, Array<string>>>;
      const storageRecentInputString = storageService.getItem(STORAGE_KEYS.RECENT_INPUT) ?? '{}';
      try {
        recentInputs = JSON.parse(storageRecentInputString);
      } catch {
        recentInputs = {};
      }
      const storageIdp = storageService.getItem(STORAGE_KEYS.IDP, 'cookie');
      let idp: AuthConfigIds | null = null;
      if (storageIdp === AuthConfigIds.ONE_LOGIN) {
        idp = AuthConfigIds.ONE_LOGIN;
      } else if (storageIdp === AuthConfigIds.AZURE) {
        idp = AuthConfigIds.AZURE;
      }
      const storageCountry = storageService.getItem(STORAGE_KEYS.COUNTRY, 'cookie')?.split('_');
      let country: UserCountry | null = null;
      let multipleCountries = null;
      if (storageCountry && storageCountry.length === 2) {
        country = storageCountry[0] === 'ES' ? 'ES' : 'PT';
        multipleCountries = storageCountry[1] === 'true';
      }
      return {
        ...state,
        user: {
          ...state.user,
          country: country,
          multipleCountries: multipleCountries,
          impersonationToken: storageImpersonationToken,
        },
        recentInput: {
          all: recentInputs,
        },
        auth: {
          ...state.auth,
          idp,
        },
        language: {
          lang: storageLanguage,
          visibilityLang: true,
        },
      };
    }

    // Update the store on each action if app was already rehydrated
    if (isRehydrated) {
      let newState: StoreState = reducer(state, action);
      if (action.type === userActions.setUser.type) {
        const user = newState.user.user;
        if (user) {
          const userString = `${user.crmCode}-${user.email}`;
          const storageUserString = storageService.getItem(STORAGE_KEYS.USER);
          if (userString !== storageUserString) {
            storageService.setItem(STORAGE_KEYS.USER, userString);
            newState = {
              ...newState,
              recentInput: {
                all: {},
              },
              language: {
                ...newState.language,
                lang: defaultLanguageFn(),
              },
            };
          }
        }
      } else if (action.type === userActions.setCountry.type) {
        newState = {
          ...storeInitialState,
          user: newState.user,
          language: newState.language,
          app: newState.app,
          auth: newState.auth,
        };
      }
      return newState;
    }

    return reducer(state, action);
  };
